import React, { createContext, isValidElement, useContext } from 'react';
import type { Provider, ReactNode } from 'react';
import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import {
	BLOCK_SEPARATOR,
	getBlockSearchText,
	holdsRevealableContent,
	isExpandNode,
} from './utils/expand-search-text';

type ExpandBody = {
	/**
	 * Opens this expand, then asks the expand above it to do the same.
	 *
	 * Browser find reveals the element holding the text it matched, and that element belongs to the
	 * expand the text is really in — even a deeply nested one, because an expand only ever replaces
	 * its own blocks with text, never a nested expand. So the expands to open are just that expand's
	 * ancestors.
	 */
	openWithAncestors: () => void;
	/**
	 * True once this expand has been opened. While it is false, the blocks of the body show
	 * their text instead of rendering.
	 *
	 * Each block reads this and decides for itself. The expand does not swap its body out,
	 * because that would throw away and rebuild everything inside it — including a nested
	 * expand find had just opened, and any nested renderer that had already loaded.
	 */
	revealed: boolean;
};

const ExpandBodyContext = createContext<ExpandBody | null>(null);

export const ExpandBodyProvider: Provider<ExpandBody | null> = ExpandBodyContext.Provider;

export const useExpandBody = (): ExpandBody | null => useContext(ExpandBodyContext);

type ExpandBodyBlockProps = {
	children: ReactNode;
	/** The block's text, or `undefined` if it must always be rendered. */
	searchText?: string;
};

/**
 * One or more neighbouring blocks of an expand's body. Shows their text until the expand is
 * opened, then renders them. Falls back to rendering if there is no text, or if there is no
 * expand above it.
 *
 * The text is rendered as-is, with no element around it. Nothing reads it but browser find,
 * which only needs the characters to be in the DOM.
 */
export const ExpandBodyBlock = ({ children, searchText }: ExpandBodyBlockProps): ReactNode => {
	const body = useExpandBody();

	if (searchText === undefined || body === null || body.revealed) {
		return <>{children}</>;
	}

	return searchText;
};

/**
 * Called for every node the serializer renders. If the node is a block of an expand's body,
 * wraps it so it can show its text instead of rendering while that expand is collapsed.
 * Everything else is returned untouched.
 *
 * A block holding an expand of its own is also returned untouched, so a table with a nested
 * expand in it, or an extension holding stashed ADF, renders even while collapsed. That
 * expand needs an element of its own, because the element browser find reveals is the only
 * way we learn the match was inside it rather than higher up. We give up the saving on those
 * blocks so the reader searches once instead of once per level.
 *
 * `ancestors` is the node's ancestor chain, nearest last.
 */
export const withExpandBodyBlock = (
	node: PMNode,
	ancestors: readonly PMNode[],
	index: number,
	serialized: JSX.Element | null,
): JSX.Element | null => {
	const parent = ancestors[ancestors.length - 1];
	if (!parent || !isExpandNode(parent)) {
		return serialized;
	}

	if (
		!isExperimentEnabled('platform_editor_defer_collapsed_expand_body') ||
		holdsRevealableContent(node)
	) {
		return serialized;
	}

	const searchText = getBlockSearchText(node);
	if (searchText === undefined) {
		return serialized;
	}

	return (
		<ExpandBodyBlock key={`expand-body-block-${index}`} searchText={searchText}>
			{serialized}
		</ExpandBodyBlock>
	);
};

/** Whatever the serializer produced for one child: an element, a string, an array of either. */
type SerializedChild = ReactNode;

const searchTextOf = (child: SerializedChild): string | undefined =>
	isValidElement(child) && child.type === ExpandBodyBlock
		? (child.props as ExpandBodyBlockProps).searchText
		: undefined;

/**
 * Joins neighbouring blocks that are showing text into one, so a run of them is a single string in
 * the DOM rather than one per block. A body of twenty paragraphs becomes one text node instead of
 * twenty.
 *
 * A block that is being rendered — a nested expand, say — ends the run, because the text either
 * side of it has to stay either side of it.
 */
export const mergeExpandBodyText = (children: SerializedChild[]): SerializedChild[] => {
	// Called for every fragment in the document, and only an expand's body has anything to merge, so
	// leave the array alone unless two neighbours are actually showing text.
	const hasRun = children.some(
		(child, index) =>
			index > 0 &&
			searchTextOf(child) !== undefined &&
			searchTextOf(children[index - 1]) !== undefined,
	);
	if (!hasRun) {
		return children;
	}

	const merged: SerializedChild[] = [];
	let run: SerializedChild[] = [];

	const endRun = () => {
		if (run.length === 0) {
			return;
		}

		if (run.length === 1) {
			merged.push(run[0]);
		} else {
			const text = run.map(searchTextOf).filter(Boolean).join(BLOCK_SEPARATOR);
			// The blocks themselves, not the wrappers around them: nesting the wrappers would show
			// each block's text again inside the joined run.
			const blocks = run.map((child) => (child as JSX.Element).props.children);

			merged.push(
				<ExpandBodyBlock key={(run[0] as JSX.Element).key ?? undefined} searchText={text}>
					{blocks}
				</ExpandBodyBlock>,
			);
		}

		run = [];
	};

	children.forEach((child) => {
		if (searchTextOf(child) === undefined) {
			endRun();
			merged.push(child);
			return;
		}
		run.push(child);
	});
	endRun();

	return merged;
};
